<template>
  <div :class="$style.rowSpacing">
    <CheckboxWithLabel
      :disabled="!isSambaAuthenticationEnabled"
      label="Enable Samba Authentication"
      :preventDefault="true"
      :tooltip="$options.tooltip"
      :value="sambaEnabled"
      @change="handleSambaAuthenticationChange"
    />
  </div>
</template>
<script>
import { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';

const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];
const userGroupModel = StoreModelMap[ItemTypeConsts.userGroup];

export default {
  name: 'SambaAuthenticationInput',

  inject: ['$validator'],

  components: {
    CheckboxWithLabel,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  computed: {
    ...mapState(userGroupModel, {
      isSambaAuthenticationEnabled: state => state.item.isSambaAuthenticationEnabled,
      sambaEnabled: state => state.item.sambaEnabled,
    }),

    ...mapGetters(directoryCollection, {
      ldapServer: 'getLdap',
      selectedDirectories: 'getSelectedItems',
    }),

    isLdapSelected() {
      return Boolean(this.ldapServer && this.selectedDirectories.includes(this.ldapServer.id));
    },
  },

  watch: {
    /**
     * Once an LDAP server is found in the DirectoryCollection then check if Samba Authentication is
     * enabled. If Samba Authentication is not enabled then the Enable Samba Authentication
     * checkbox will be disabled. Note that this check _must_ be run after the directories have
     * been fetched because Samba Authentication is a property of LDAP. i.e.
     * GET /api/v2/ldapservers/${ldapServerId}/sambadomains is called to check if Samba
     * Authentication is enabled.
     */
    ldapServer(value) {
      if (value && value.id) {
        this.checkIsSambaAuthenticationEnabled({ ldapServerId: value.id });
      }
    },

    /**
     * When Samba is enabled and the selected directories no longer contain LDAP then prompt the
     * admin to confirm if they would like to continue, and if so then set Samba enabled to false.
     * If they choose to cancel, then reselect LDAP.
     */
    selectedDirectories() {
      if (this.sambaEnabled
          && this.ldapServer
          && !this.selectedDirectories.includes(this.ldapServer.id)) {
        this.confirmUnbindLdapFromUserGroup();
      }
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(userGroupModel, ['checkIsSambaAuthenticationEnabled']),

    ...mapActions(directoryCollection, { selectDirectory: 'selectItem' }),

    confirmBindUserGroupToLdap() {
      return new Promise((resolve) => {
        const componentProps = {
          mdTemplate: 'In order to Enable Samba Authentication, this User Group will also be enabled on the JumpCloud LDAP Directory.',
          cancelText: 'cancel',
          continueText: 'Enable',
          handleCancel: () => {
            ModalEventBus.close();
            resolve(false);
          },
          handleContinue: () => {
            ModalEventBus.close();
            resolve(true);
          },
          headerText: 'Complete Samba Configuration',
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
        });
      });
    },

    confirmSambaSecurityWarning() {
      return new Promise((resolve) => {
        const componentProps = {
          mdTemplate: 'By enabling User Groups to be authenticated to your Samba server\'s file share, JumpCloud must generate and store an MD4 password hash, a requirement for NTLM-based authentication.',
          cancelText: 'cancel',
          continueText: 'Enable',
          handleCancel: () => {
            ModalEventBus.close();
            resolve(false);
          },
          handleContinue: () => {
            ModalEventBus.close();
            resolve(true);
          },
          headerText: 'Security Warning',
          kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
        });
      });
    },

    confirmUnbindLdapFromUserGroup() {
      const componentProps = {
        mdTemplate: 'This User Group has been enabled for Samba Authentication. Removing it from the JumpCloud LDAP Directory will also remove Samba Authentication privileges.',
        cancelText: 'cancel',
        continueText: 'Continue',
        handleCancel: () => {
          this.selectLdap();
          ModalEventBus.close();
        },
        handleContinue: () => {
          this.setItemField({ key: 'sambaEnabled', value: false });
          ModalEventBus.close();
        },
        headerText: 'Samba Authentication',
      };

      ModalEventBus.open({
        isLargeModal: false,
        component: 'ConfirmationModal',
        componentProps,
      });
    },

    async handleSambaAuthenticationChange(checked) {
      if (checked === true) {
        // Prompt admin to confirm that they want to enable Samba Authentication
        let canEnableSamba = await this.confirmSambaSecurityWarning();

        // If LDAP is not already selected then prompt admin to confirm that they want to bind this
        // User Group to LDAP.
        if (canEnableSamba && !this.isLdapSelected) {
          canEnableSamba = await this.confirmBindUserGroupToLdap();

          if (canEnableSamba) {
            // If the admin chooses to continue enabling Samba then select the LDAP directory so
            // that this User Group is bound to LDAP.
            this.selectLdap();
          }
        }

        // Just return if the admin chose to cancel in either of the dialogs.
        if (!canEnableSamba) {
          return;
        }
      }

      this.setItemField({ key: 'sambaEnabled', value: checked });
    },

    selectLdap() {
      const { id } = this.ldapServer;
      this.selectDirectory({ id });
    },
  },

  tooltip: {
    kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
    text: 'Samba Authentication must be configured in the JumpCloud LDAP Directory and LDAP sync must be enabled on this group before Samba Authentication can be enabled.',
  },
};
</script>
<style module>
@import '../../components/FormValidation/css/formFields.css';
</style>
